import {
  loadSettings,
  saveSettings,
  MASTRA_GATEWAY_PROVIDER,
  MASTRA_GATEWAY_DEFAULT_URL,
} from '@mastra/code-sdk/onboarding/settings';
import { GatewayRegistry } from '@mastra/core/llm';
import { askModalQuestion } from '../modal-question.js';
import type { SlashCommandContext } from './types.js';

async function askText(ctx: SlashCommandContext, question: string, defaultValue?: string): Promise<string | null> {
  const answer = await askModalQuestion(ctx.state.ui, { question, defaultValue });
  const trimmed = answer?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

async function askSelect(
  ctx: SlashCommandContext,
  question: string,
  options: Array<{ label: string; value: string; description?: string }>,
): Promise<string | null> {
  const answer = await askModalQuestion(ctx.state.ui, {
    question,
    options: options.map(option => ({ label: option.label, description: option.description })),
  });
  const selected = options.find(option => option.label === answer);
  const trimmed = answer?.trim() ?? '';
  return selected?.value ?? (trimmed.length > 0 ? trimmed : null);
}

async function refreshGatewayModels(ctx: SlashCommandContext): Promise<void> {
  try {
    await GatewayRegistry.getInstance({ useDynamicLoading: true }).syncGateways(true);
  } catch (error) {
    ctx.showError(`Failed to refresh gateway models: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export async function handleMastraGatewayCommand(ctx: SlashCommandContext): Promise<void> {
  const authStorage = ctx.authStorage;
  if (!authStorage) {
    ctx.showError('Auth storage not available');
    return;
  }

  // Resolve effective state from storage + env
  const currentKey = authStorage.getStoredApiKey(MASTRA_GATEWAY_PROVIDER) ?? process.env['MASTRA_GATEWAY_API_KEY'];
  const settings = loadSettings();
  const effectiveUrl =
    settings.memoryGateway?.baseUrl ?? process.env['MASTRA_GATEWAY_URL'] ?? MASTRA_GATEWAY_DEFAULT_URL;

  if (currentKey) {
    const masked = currentKey.length > 6 ? `****${currentKey.slice(-4)}` : '****';
    ctx.showInfo(`Current API key: ${masked} | URL: ${effectiveUrl}`);
  } else {
    ctx.showInfo(`No API key set | URL: ${effectiveUrl}`);
  }

  // Ask for API key
  const keyAnswer = await askText(
    ctx,
    currentKey
      ? `API key (ENTER to keep current, 'clear' to remove, ESC to skip):`
      : `API key (or 'clear' to remove, ESC to cancel):`,
    currentKey,
  );
  if (keyAnswer === null) {
    // ESC with no key — abort; ESC with existing key — proceed to URL prompt
    if (!currentKey) return;
  } else if (keyAnswer.toLowerCase() === 'clear') {
    authStorage.remove(`apikey:${MASTRA_GATEWAY_PROVIDER}`);
    delete process.env['MASTRA_GATEWAY_API_KEY'];
    delete process.env['MASTRA_GATEWAY_URL'];
    settings.memoryGateway = {};
    saveSettings(settings);
    await refreshGatewayModels(ctx);
    ctx.showInfo('Gateway cleared. Memory mode changes take effect on next restart.');
    return;
  } else if (keyAnswer.length > 0) {
    authStorage.setStoredApiKey(MASTRA_GATEWAY_PROVIDER, keyAnswer, 'MASTRA_GATEWAY_API_KEY');
  }

  const urlChoice = await askSelect(ctx, 'Gateway URL', [
    {
      label: MASTRA_GATEWAY_DEFAULT_URL,
      value: MASTRA_GATEWAY_DEFAULT_URL,
      description: effectiveUrl === MASTRA_GATEWAY_DEFAULT_URL ? 'current' : 'hosted default',
    },
    {
      label: 'http://localhost:4111',
      value: 'http://localhost:4111',
      description: effectiveUrl === 'http://localhost:4111' ? 'current' : 'local development',
    },
  ]);

  if (urlChoice === null) {
    return;
  }

  const urlAnswer = urlChoice;

  if (urlAnswer && urlAnswer !== MASTRA_GATEWAY_DEFAULT_URL) {
    settings.memoryGateway = { baseUrl: urlAnswer };
    process.env['MASTRA_GATEWAY_URL'] = urlAnswer;
  } else {
    settings.memoryGateway = {};
    delete process.env['MASTRA_GATEWAY_URL'];
  }
  saveSettings(settings);
  await refreshGatewayModels(ctx);

  ctx.showInfo('Gateway configured. Memory mode changes take effect on next restart.');
}
